/**
 * [LOAD-STATES 2026-09-29, user] NEW FILE — what a tab shows INSTEAD of its data.
 *
 * Before this, a failed load looked like real data on 8 tabs ($0.00 / "No … found"), showed a raw
 * "Internal server error" on 7, a message with no retry on 4, and Retention Analysis loaded forever.
 * The user picked these looks from a live template (artifact 8sHCvPQhCGsCxGvkgVXs9r):
 *
 *   LoadFailed  (F1) — a small card with a countdown ring: "Couldn't load payments · Trying again in 5 s".
 *                      Retries BY ITSELF after 5 s, 10 s, 20 s, then every 30 s; at once when the internet
 *                      comes back or the tab is looked at again; paused while the browser tab is hidden.
 *                      "Try now" skips the wait.
 *   EmptyState  (E1) — icon, one line, one button: "No payments match "xyz"" + "Clear search".
 *   LoadCrash        — the per-tab crash screen shown by ErrorBoundary (a bug, not the server).
 *
 * How LoadFailed knows a retry worked: it calls the page's own load and does nothing else. If the load
 * succeeds the page stops rendering this card (so it unmounts); if the card is still on screen after the
 * load settles, the load failed again and the next, longer countdown starts. The attempt count lives in a
 * module map keyed by `what`, so a page that swaps in its skeleton while retrying (unmounting the card)
 * still backs off properly. A failure more than 90 s after the last one starts again at 5 s.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const DELAYS_S = [5, 10, 20, 30];
const STALE_MS = 90_000;
const attempts = new Map<string, { n: number; last: number }>();

const nextAttempt = (key: string): number => {
  const now = Date.now();
  const prev = attempts.get(key);
  // Same failure counted twice within a second = React StrictMode's double render in dev, not a
  // second attempt (a real retry is always a countdown or a "Try now" + the load time later).
  if (prev && now - prev.last < 1000) return prev.n;
  const n = prev && now - prev.last < STALE_MS ? prev.n + 1 : 1;
  attempts.set(key, { n, last: now });
  return n;
};
const delayFor = (attempt: number) => DELAYS_S[Math.min(attempt - 1, DELAYS_S.length - 1)];

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const spin = keyframes`to { transform: rotate(270deg); }`;

const Wrap = styled.div<{ $compact?: boolean }>`
  display: grid;
  place-items: center;
  padding: ${(p) => (p.$compact ? '20px 16px' : '48px 20px')};
  animation: ${fadeIn} 200ms cubic-bezier(0.16, 1, 0.3, 1) both;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Card = styled.div`
  display: grid;
  justify-items: center;
  gap: 6px;
  text-align: center;
  max-width: 340px;
`;

const Ring = styled.div<{ $spin: boolean }>`
  position: relative;
  width: 52px;
  height: 52px;
  margin-bottom: 4px;
  svg { width: 52px; height: 52px; transform: rotate(-90deg); }
  svg { animation: ${(p) => (p.$spin ? spin : 'none')} 0.9s linear infinite; }
  .trk { stroke: ${(p) => p.theme.pill}; }
  .arc { stroke: ${(p) => p.theme.greenInk}; transition: stroke-dashoffset 1s linear; }
  .n {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    font-size: 16px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    color: ${(p) => p.theme.text};
  }
  @media (prefers-reduced-motion: reduce) {
    svg { animation: none; }
    .arc { transition: none; }
  }
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: ${(p) => p.theme.text};
`;

/* Full ink on purpose: the app's rule is no grey secondary text (theme.muted is full ink). */
const Line = styled.div`
  font-size: 12.5px;
  line-height: 1.45;
  color: ${(p) => p.theme.muted};
`;

const Small = styled.div`
  font-size: 11.5px;
  color: ${(p) => p.theme.muted};
  opacity: 0.8;
`;

/* The app's main green button (D2 "Glass gradient", the "+ Add" recipe the user picked). */
const PrimaryBtn = styled.button`
  margin-top: 6px;
  height: 32px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(70, 191, 130, 0.55)' : 'rgba(26, 159, 125, 0.55)')};
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(70, 191, 130, 0.55) 0%, rgba(61, 158, 108, 0.65) 100%)'
    : 'linear-gradient(180deg, rgba(13, 193, 152, 0.85) 0%, rgba(26, 159, 125, 0.92) 100%)')};
  color: #fff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow:
    0 6px 18px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(61, 158, 108, 0.45)' : 'rgba(26, 159, 125, 0.35)')},
    inset 0 1px 0 rgba(255, 255, 255, 0.45),
    inset 0 -1px 0 rgba(0, 0, 0, 0.12);
  transition: filter 120ms cubic-bezier(0.4, 0, 0.2, 1), transform 120ms cubic-bezier(0.4, 0, 0.2, 1);
  &:hover:not(:disabled) { filter: brightness(1.06); }
  &:active:not(:disabled) { transform: translateY(1px); }
  &:disabled { opacity: 0.6; cursor: default; }
  /* [PHONE-LAYOUT 2026-10-22] Phones: a 40px tall tap target. */
  @media (max-width: 768px) {
    height: 40px;
    padding: 0 20px;
  }
`;

/* The app's plain button (D2 glass white). */
const SecondaryBtn = styled.button`
  margin-top: 6px;
  height: 32px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.65)')};
  color: ${(p) => p.theme.text};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.80)')};
  transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1), border-color 120ms cubic-bezier(0.4, 0, 0.2, 1);
  &:hover {
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.85)')};
    border-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.35)' : 'rgba(26,159,125,0.30)')};
  }
  /* [PHONE-LAYOUT 2026-10-22] Phones: a 40px tall tap target. */
  @media (max-width: 768px) {
    height: 40px;
    padding: 0 18px;
  }
`;

const IconDisc = styled.div<{ $tone: 'green' | 'amber' }>`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  margin-bottom: 4px;
  font-size: 16px;
  background: ${(p) => (p.$tone === 'green'
    ? p.theme.greenTint
    : p.theme.colorScheme === 'dark' ? 'rgba(245,158,11,0.14)' : 'rgba(245,158,11,0.16)')};
  color: ${(p) => (p.$tone === 'green' ? p.theme.greenInk : p.theme.colorScheme === 'dark' ? '#f5b453' : '#8a4b06')};
`;

/** A 403 ("you may not see this") is a final answer, not a failed load: pages show it as a plain
 *  message and never count down to retry it. */
export const isForbidden = (e: unknown): boolean => {
  const r = (e as any)?.response;
  return Number(r?.status) === 403 || r?.data?.error === 'section_forbidden';
};

export interface LoadFailedProps {
  /** Lower-case name of what failed: "payments" → "Couldn't load payments". Also the backoff key. */
  what: string;
  /** The page's own load. May return a promise; its result is ignored (see the file header). */
  onRetry: () => unknown;
  /** Less vertical padding, for a card-sized area rather than a whole table. */
  compact?: boolean;
}

/** F1 — "Couldn't load …" with a countdown ring and an automatic retry. */
export const LoadFailed: React.FC<LoadFailedProps> = ({ what, onRetry, compact }) => {
  const [attempt, setAttempt] = useState(() => nextAttempt(what));
  const [left, setLeft] = useState(() => delayFor(attempt));
  const [retrying, setRetrying] = useState(false);
  const busy = useRef(false);
  const mounted = useRef(true);
  const onRetryRef = useRef(onRetry);
  onRetryRef.current = onRetry;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const retry = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setRetrying(true);
    try { await Promise.resolve(onRetryRef.current()); } catch { /* the page shows its own failure */ }
    // Give the page one beat to render its result: success unmounts this card.
    await new Promise((r) => setTimeout(r, 450));
    busy.current = false;
    if (!mounted.current) return;
    const n = nextAttempt(what);
    setAttempt(n);
    setLeft(delayFor(n));
    setRetrying(false);
  }, [what]);

  // One tick a second; a hidden browser tab does not count down (and so never retries unseen).
  useEffect(() => {
    if (retrying) return undefined;
    const id = window.setInterval(() => { if (!document.hidden) setLeft((s) => s - 1); }, 1000);
    return () => window.clearInterval(id);
  }, [retrying, attempt]);
  useEffect(() => { if (!retrying && left <= 0) void retry(); }, [left, retrying, retry]);

  // The internet came back, or the person came back to this browser tab: try at once.
  useEffect(() => {
    const now = () => { if (!document.hidden) void retry(); };
    window.addEventListener('online', now);
    document.addEventListener('visibilitychange', now);
    return () => {
      window.removeEventListener('online', now);
      document.removeEventListener('visibilitychange', now);
    };
  }, [retry]);

  const total = delayFor(attempt);
  const C = 2 * Math.PI * 22;
  const shown = Math.max(0, left);
  return (
    <Wrap $compact={compact}>
      <Card role="status" aria-live="polite">
        <Ring $spin={retrying} aria-hidden="true">
          <svg viewBox="0 0 52 52">
            <circle className="trk" cx="26" cy="26" r="22" fill="none" strokeWidth="4" />
            <circle
              className="arc"
              cx="26"
              cy="26"
              r="22"
              fill="none"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={retrying ? `${C * 0.22} ${C}` : `${C}`}
              strokeDashoffset={retrying ? 0 : C * (1 - shown / total)}
            />
          </svg>
          <span className="n">{retrying ? '' : shown}</span>
        </Ring>
        <Title>Couldn't load {what}</Title>
        <Line>{retrying ? 'Trying now…' : `Trying again in ${shown} s.`}</Line>
        <Small>Attempt {attempt} · nothing is lost, this only affects loading</Small>
        <PrimaryBtn type="button" onClick={() => void retry()} disabled={retrying}>Try now</PrimaryBtn>
      </Card>
    </Wrap>
  );
};

export interface EmptyStateProps {
  /** One line that says exactly what is empty: "No refunds in September 2026". */
  title: string;
  /** Optional second line: what to do about it. */
  hint?: string;
  /** Optional fix, e.g. { label: 'Clear search', onClick }. */
  action?: { label: string; onClick: () => void };
  /** 'search' when a search/filter found nothing; 'empty' when there is simply no data. */
  icon?: 'search' | 'empty';
  compact?: boolean;
}

/** E1 — icon, one line, one button. */
export const EmptyState: React.FC<EmptyStateProps> = ({ title, hint, action, icon = 'empty', compact }) => (
  <Wrap $compact={compact}>
    <Card>
      <IconDisc $tone="green" aria-hidden="true">
        <i className={icon === 'search' ? 'fas fa-magnifying-glass' : 'fas fa-inbox'} />
      </IconDisc>
      <Title>{title}</Title>
      {hint && <Line>{hint}</Line>}
      {action && <SecondaryBtn type="button" onClick={action.onClick}>{action.label}</SecondaryBtn>}
    </Card>
  </Wrap>
);

/** The crash screen ErrorBoundary shows for one tab; the rest of the dashboard keeps working. */
export const LoadCrash: React.FC<{ onReload: () => void }> = ({ onReload }) => (
  <Wrap>
    <Card role="alert">
      <IconDisc $tone="amber" aria-hidden="true"><i className="fas fa-triangle-exclamation" /></IconDisc>
      <Title>This section hit a problem</Title>
      <Line>The other tabs still work. Reloading usually fixes it.</Line>
      <PrimaryBtn type="button" onClick={onReload}>Reload section</PrimaryBtn>
    </Card>
  </Wrap>
);
